/**
 * ProcessingStatusIcon - Status icon for Processing Flow events
 *
 * Displays appropriate icon and color based on event status
 * Based on PROCESSING_FLOW_UI_DESIGN.md Section 2.10
 */

import React from 'react';
import {
  Check,
  Circle,
  AlertTriangle,
  Pause,
  X,
  Loader2,
} from 'lucide-react';
import { EventStatus } from '../../../types/orderProcessingFlow';
import { cn } from '../../../lib/utils';

interface ProcessingStatusIconProps {
  status: EventStatus;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  pulse?: boolean;
}

const sizeClasses = {
  sm: 'w-5 h-5',
  md: 'w-7 h-7',
  lg: 'w-9 h-9',
};

const iconSizeClasses = {
  sm: 'w-2.5 h-2.5',
  md: 'w-3.5 h-3.5',
  lg: 'w-4.5 h-4.5',
};

// Colors that align with the modal's teal-based design
const STATUS_COLORS: Record<EventStatus, string> = {
  completed: '#2E7D32',    // success green
  in_progress: '#00838F',  // teal (matches modal primary)
  pending: 'transparent',
  warning: '#F57C00',
  paused: '#7B1FA2',
  failed: '#C62828',
};

export const ProcessingStatusIcon: React.FC<ProcessingStatusIconProps> = ({
  status,
  size = 'md',
  className,
  pulse = false,
}) => {
  const color = STATUS_COLORS[status];

  const getIcon = () => {
    const iconClass = cn(iconSizeClasses[size], 'text-white');

    switch (status) {
      case 'completed':
        return <Check className={iconClass} />;
      case 'in_progress':
        return pulse ? (
          <Loader2 className={cn(iconClass, 'animate-spin')} />
        ) : (
          <Circle className={iconClass} fill="white" />
        );
      case 'pending':
        return <Circle className={cn(iconClass, 'text-gray-300')} />;
      case 'warning':
        return <AlertTriangle className={iconClass} />;
      case 'paused':
        return <Pause className={iconClass} />;
      case 'failed':
        return <X className={iconClass} />;
      default:
        return <Circle className={iconClass} />;
    }
  };

  const getBackgroundStyle = () => {
    if (status === 'pending') {
      return {
        backgroundColor: 'white',
        border: '2px solid #D1D5DB',
      };
    }
    return {
      backgroundColor: color,
    };
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full flex-shrink-0 transition-all',
        sizeClasses[size],
        status === 'in_progress' && pulse && 'ring-3 ring-[#E0F7FA] shadow-md shadow-[#00838F]/20',
        className
      )}
      style={getBackgroundStyle()}
    >
      {getIcon()}
    </div>
  );
};
